import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ChangeDetectionStrategy
} from '@angular/core'
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy'

import { MatMenuTrigger } from '@angular/material/menu'
import {
  AbstractControl,
  FormBuilder,
  UntypedFormArray,
  UntypedFormGroup,
  Validators
} from '@angular/forms'
import {
  isActiveRule,
  isEmptinessOperator,
  New_EmptinessOperators,
  New_NumberOperators,
  New_NumberOperatorSymbols,
  New_StringOperators,
  New_TableOperatorToSentence
} from './table-filter.constant'
import { Sort, SortDirection } from '@angular/material/sort'
import { ISelectItem } from '@lla-platform/core/core-ui'
import {
  New_AggregatorType,
  INew_TableFilter,
  INew_TableFIlterNotifier,
  INew_TableFilterRule,
  New_TableColumnType,
  New_TableOperator,
  IRangeDateInfo,
  PeriodType,
  FilterStateKeys,
  FiltersState
} from '@lla-platform/core/core-data-access'
import { Observable } from 'rxjs'
import { Store } from '@ngxs/store'
import { TippyProps } from '../../directives/lla-tippy.directive'

@UntilDestroy()
@Component({
  selector: 'lla-table-filter',
  templateUrl: './table-filter.component.html',
  styleUrls: ['./table-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class TableFilterComponent implements OnInit, OnChanges {
  @ViewChild(MatMenuTrigger) menu: MatMenuTrigger

  @Input() advancedFilterKey: FilterStateKeys
  @Input() filter: INew_TableFilter
  @Input() sort?: Sort | null
  @Input() onlySort = false
  @Input() rightAlign = false
  @Input() centerAlign = false
  @Input() extraClass = ''
  @Input() extraTriggerClass = ''
  @Input() forOverview = false
  @Input() showSortArrow = true
  @Input() showAppliedRules = false
  // Always render the advanced filter UI, for pages that have no advanced-mode toggle
  @Input() forceAdvancedMode = false

  @Output() filterChanged = new EventEmitter<INew_TableFIlterNotifier>()

  isAdvancedMode = false
  appliedRulesText?: string

  // Add a private counter for unique IDs
  private ruleIdCounter = 0

  sortDirection?: SortDirection
  tableColumnTypes = New_TableColumnType

  visibleListItems: ISelectItem[] = []
  form: UntypedFormGroup
  aggregatorControl: AbstractControl | null
  itemsControl: UntypedFormArray
  operators: ISelectItem[]
  defaultOperator: New_TableOperator
  protected readonly isEmptinessOperator = isEmptinessOperator
  aggregatorItems: ISelectItem[] = [
    {
      label: 'Match any',
      value: New_AggregatorType.Or
    },
    {
      label: 'Match all',
      value: New_AggregatorType.And
    }
  ]
  defaultAggregator: New_AggregatorType = New_AggregatorType.Or
  selectedDateInfo: IRangeDateInfo

  tooltipOption: Partial<TippyProps> = {
    arrow: false,
    hideOnClick: true,
    placement: 'bottom-start'
  }

  constructor(
    private fb: FormBuilder,
    private store: Store
  ) {}

  ngOnInit(): void {
    if (this.forceAdvancedMode) {
      this.isAdvancedMode = true
    } else if (this.advancedFilterKey) {
      const isAdvancedMode$: Observable<boolean> = this.store.select(
        (FiltersState as any)[this.advancedFilterKey]
      )

      isAdvancedMode$.pipe(untilDestroyed(this)).subscribe((res) => {
        this.isAdvancedMode = res
        if (
          this.isAdvancedMode &&
          this.itemsControl &&
          this.itemsControl.length === 2 &&
          this.hasEmptinessCheckbox()
        ) {
          this.itemsControl.at(0).get('value')?.enable()
          this.itemsControl.at(1).get('value')?.enable()
        }
      })
    }

    this.visibleListItems = [...(this.filter.listItems ?? [])]

    switch (this.filter.columnType) {
      case New_TableColumnType.text:
        this.operators = New_StringOperators
        this.defaultOperator = New_TableOperator.StartsWith
        break

      case New_TableColumnType.number:
        this.operators = this.filter.nullable
          ? [...New_NumberOperators, ...New_EmptinessOperators]
          : New_NumberOperators
        this.defaultOperator = New_TableOperator.Equals
        this.defaultAggregator = New_AggregatorType.And
        break

      case New_TableColumnType.checkbox:
      case New_TableColumnType.radio:
        this.defaultOperator = New_TableOperator.Equals
        break

      case New_TableColumnType.date:
        this.defaultOperator = New_TableOperator.Period
        this.defaultAggregator = New_AggregatorType.And
        break
    }

    this.form = this.fb.group({
      aggregator: [this.filter.aggregator],
      items: this.fb.array([]),
      searchText: [undefined]
    })

    this.aggregatorControl = this.form.get('aggregator')
    this.itemsControl = this.form.get('items') as UntypedFormArray

    this.form
      .get('searchText')
      ?.valueChanges.pipe(untilDestroyed(this))
      .subscribe((value: string) => {
        const trimValue = value?.trim().toLowerCase()
        if (trimValue && trimValue !== '') {
          this.visibleListItems =
            this.filter.listItems?.filter((el) => el.label.toLowerCase().includes(trimValue)) ?? []
        } else {
          this.visibleListItems = [...(this.filter.listItems ?? [])]
        }
      })
    this.updateForm()
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sort']) {
      this.sortDirection =
        this.sort?.active === (this.filter.sortColumnName ?? this.filter.key)
          ? this.sort.direction
          : undefined
    }
    if (changes['filter']) {
      this.updateAppliedRulesText()
    }
  }

  private updateAppliedRulesText() {
    this.appliedRulesText = this.showAppliedRules ? this.new_NumberRulesSummary(this.filter) : undefined
  }

  updateForm() {
    this.itemsControl.clear()
    this.aggregatorControl?.setValue(this.filter.aggregator ?? this.defaultAggregator)
    if (!this.filter?.items || this.filter.items.length < 1) {
      this.addNewRule()
    } else {
      for (const item of this.filter.items) {
        this.addNewRule(item)
      }
      if (this.itemsControl?.value?.length === 1 && this.hasEmptinessCheckbox()) {
        this.addNewRule()
        setTimeout(() => {
          if (!this.isAdvancedMode) {
            this.itemsControl.at(1).get('value')?.disable()
          }
        }, 200)
      }
    }
  }

  onMenuClose(): void {
    setTimeout(() => {
      this.updateForm()
    }, 200)
  }

  public openMenu(): void {
    this.menu.openMenu()
  }

  addNewRule(item?: INew_TableFilterRule) {
    if (item?.extraValue && this.filter.columnType === New_TableColumnType.date) {
      this.selectedDateInfo = {
        periodType: item?.value ?? PeriodType.Custom,
        rangeDate: item.extraValue
      }
    }

    const formGroup = this.fb.group({
      ruleId: [++this.ruleIdCounter], // Add unique ID
      operator: [item?.operator ?? this.defaultOperator],
      value: [item?.value, Validators.required],
      extraValue: [item?.extraValue]
    })

    this.itemsControl.push(formGroup)
  }

  clearFilters() {
    this.itemsControl.clear()
    this.addNewRule()
    this.filterChanged.emit({
      filter: { ...this.filter, items: [], columnType: this.filter.columnType }
    })
  }

  removeRule(index: number) {
    this.itemsControl.removeAt(index)
    if (
      this.itemsControl?.value?.length === 0 ||
      (this.itemsControl?.value?.length === 1 && this.hasEmptinessCheckbox())
    ) {
      this.addNewRule()
    }
  }

  onDateChange(
    initial: boolean,
    valueControl: AbstractControl,
    extraValueControl: AbstractControl,
    event: IRangeDateInfo
  ) {
    if (initial) {
      this.selectedDateInfo = event
    }
    if (event.rangeDate?.from && event.rangeDate?.to) {
      valueControl.setValue(event.periodType ?? PeriodType.Custom)
      extraValueControl.setValue({
        from: event.rangeDate.from,
        to: event.rangeDate.to
      })
    }
  }

  submit() {
    this.form.markAllAsTouched()
    if (this.form.valid || this.hasEmptinessRule()) {
      const newFilters: INew_TableFilter = this.form.value
      this.filterChanged.emit({
        filter: {
          ...this.filter,
          ...{
            ...newFilters,
            items: newFilters?.items?.filter(isActiveRule)
          },
          columnType: this.filter.columnType
        }
      })
      this.menu.closeMenu()
    }
  }

  hasEmptyRule() {
    return this.findRuleByOperator(New_TableOperator.IsEmpty)
  }

  hasNotEmptyRule() {
    return this.findRuleByOperator(New_TableOperator.IsNotEmpty)
  }

  setHasEmptyRule(value: boolean) {
    this.toggleEmptinessRule(New_TableOperator.IsEmpty, value)
  }

  setHasNotEmptyRule(value: boolean) {
    this.toggleEmptinessRule(New_TableOperator.IsNotEmpty, value)
  }

  private findRuleByOperator(operator: New_TableOperator) {
    return this.itemsControl?.value?.find((item: INew_TableFilterRule) => item.operator === operator)
  }

  // True when either emptiness operator is active — those rules carry no user-entered value
  private hasEmptinessRule() {
    return this.hasEmptyRule() || this.hasNotEmptyRule()
  }

  // Text columns toggle emptiness with checkboxes below a disabled value rule
  private hasEmptinessCheckbox() {
    return this.filter.columnType === New_TableColumnType.text && this.hasEmptinessRule()
  }

  private removeRuleByOperator(operator: New_TableOperator) {
    const index = this.itemsControl.value.findIndex(
      (item: INew_TableFilterRule) => item.operator === operator
    )
    if (index > -1) {
      this.itemsControl.removeAt(index)
    }
  }

  private toggleEmptinessRule(operator: New_TableOperator, checked: boolean) {
    const opposite =
      operator === New_TableOperator.IsEmpty ? New_TableOperator.IsNotEmpty : New_TableOperator.IsEmpty

    if (checked) {
      this.removeRuleByOperator(opposite)
      if (this.findRuleByOperator(operator)) {
        return
      }
      if (!this.isAdvancedMode && this.itemsControl.value?.length === 1) {
        this.itemsControl.at(0).get('value')?.setValue('')
        this.itemsControl.at(0).get('value')?.disable()
      }
      this.itemsControl.push(
        this.fb.group({
          operator: [operator],
          value: ['']
        })
      )
    } else {
      this.removeRuleByOperator(operator)
      if (!this.isAdvancedMode && this.itemsControl.value?.length === 1) {
        this.itemsControl.at(0).get('value')?.enable()
      }
    }
  }

  // Compact one-line summary of a number filter's applied rules, e.g. "> 200 & < 1,000"
  private new_NumberRulesSummary(filter?: INew_TableFilter): string | undefined {
    if (filter?.columnType !== New_TableColumnType.number) {
      return undefined
    }
    const rules = (filter.items ?? []).filter(isActiveRule)
    if (rules.length === 0) {
      return undefined
    }
    const texts = rules.map((el) => {
      if (isEmptinessOperator(el.operator)) {
        return New_TableOperatorToSentence[el.operator]
      }
      const value = Number(el.value)
      return `${New_NumberOperatorSymbols[el.operator] ?? ''} ${
        isNaN(value) ? el.value : value.toLocaleString('en-US')
      }`
    })
    return texts.join(filter.aggregator === New_AggregatorType.And ? ' & ' : ' or ')
  }
}
